import { lazy, Suspense } from 'react';
import { useTheme, css } from '@emotion/react';
import { Rating } from '../Rating/Rating';

const DynamicInfoPopover = lazy(() => import('../DynamicPopover/DynamicInfoPopover'));

interface SkuItem {
  expandedItem: {
    averageOverallRating?: string | number;
    productType?: string;
  };
}

interface AverageCaseRatingProps {
  referencedSkus: SkuItem[];
  minRatingToShow?: number;
}

export function calculateAverageRating(filteredSkus: SkuItem[]): number | null {
  // Filter ratings that are defined and then convert them to numbers
  const ratings = filteredSkus
    .map(({ expandedItem }) => parseFloat(expandedItem.averageOverallRating as string))
    .filter(rating => !isNaN(rating));

  if (ratings.length === 0) {
    return null;
  }

  const sum = ratings.reduce((acc, curr) => acc + curr, 0);
  const average = sum / ratings.length;

  return parseFloat(average.toFixed(1));
}

export const AverageCaseRating = ({ referencedSkus, minRatingToShow = 0 }: AverageCaseRatingProps) => {
  const theme = useTheme();

  if (!referencedSkus) {
    return null;
  }

  const averageCaseRatingStyle = css`
    ${theme.widgets.Product?.averageCaseRating}
  `;

  // Filter out Skus marked as "Literature"
  const filteredSkus = referencedSkus.filter(({ expandedItem }) => expandedItem.productType !== 'Literature');

  const averageRating = calculateAverageRating(filteredSkus);

  return averageRating !== null && averageRating >= minRatingToShow ? (
    <div css={averageCaseRatingStyle} className="average-case-rating">
      <div>
        <Rating rating={averageRating} />
      </div>
      <div>{averageRating} Average bottle rating</div>
      <div>
        <Suspense fallback={null}>
          <DynamicInfoPopover text="Indicates average (combined) bottle rating of each wine in this collection." />
        </Suspense>
      </div>
    </div>
  ) : null;
};
